import { useAuth } from '@/context/AuthContext';
import { useUnriddle } from '@/context/UnriddleContext';
import { useImpersonation } from '@/hooks/useImpersonation';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { isAnaraTeamMember } from '@/lib/fingerprint';
import { api } from '@/lib/trpc/react';
import { getPriceId } from '@/utils/billingHelpers';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import * as Sentry from '@sentry/nextjs';
import { TagIcon, X } from 'lucide-react';
import { motion } from 'motion/react';
import { usePathname } from 'next/navigation';
import { toast } from 'sonner';
import { Button } from '../Button/Button';
import { LoadingSpinner } from '../Loading';
import { Icon } from '../ui/Icon';

export const FreeUserModal = () => {
  const { setShowFreeUserModal } = useUnriddle();
  const { track } = useAnalytics();
  const { checkAdmin } = useImpersonation();
  const { user } = useAuth();
  const { getData } = useVisitorData(
    { extendedResult: true },
    {
      immediate: false,
    },
  );
  const { mutateAsync: accountByVisitorId } =
    api.fingerprint.accountByVisitorId.useMutation();

  const { mutateAsync: createSubscription, isPending } =
    api.subscriptionRouter.create.useMutation();

  const path = usePathname();

  const handleClose = async () => {
    localStorage?.setItem('freeUserModalClosed', 'true');
    setShowFreeUserModal(false);
  };

  const onSubmit = async () => {
    const admin = await checkAdmin(user?.email ?? '');
    if (admin) {
      return;
    }

    track('free_user_promotion_modal_clicked', {
      method: 'Submit',
    });

    if (!isAnaraTeamMember(user?.email, user?.uniqueVisitorId)) {
      const verifiedData = await getData();

      if (
        verifiedData.visitorId &&
        !isAnaraTeamMember(user?.email, verifiedData.visitorId)
      ) {
        const accounts = await accountByVisitorId({
          visitorId: verifiedData.visitorId,
        });

        if (accounts.length >= 3 && !accounts.includes(user?.id ?? '')) {
          track('device_account_limit_reached', {
            from: 'free_user_modal',
            userId: user?.id,
            visitorId: verifiedData.visitorId,
          });
          toast.error(
            <span>
              Maximum number of accounts for this device reached. Please{' '}
              <button
                className="cursor-pointer underline"
                id="intercom_support"
              >
                contact support
              </button>{' '}
              if this is a mistake.
            </span>,
            {
              duration: 7000,
            },
          );
        }
      } else {
        Sentry.captureMessage('No visitorId found');
      }
    }

    const priceId = getPriceId('pro', 'monthly');

    if (!priceId) {
      return;
    }

    const url = await createSubscription({
      quantity: 1,
      priceId: priceId,
      referralId: null,
      plan: 'PRO',
      billingInterval: 'monthly',
      successPath: path || 'new',
      cancelPath: path || 'new',
      metadata: true,
    });

    if (!url) {
      Sentry.captureException('No url returned from createSubscription');
      return;
    }

    window.location.href = url;

    handleClose();
  };

  return (
    <motion.div
      className="bg-popover shadow-feint rounded-xl fixed left-4 bottom-4 h-[360px] w-[328px] border border-border z-[45]"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={{
        initial: { opacity: 0, x: -100 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -100 },
      }}
      transition={{ ease: 'easeOut', duration: 0.3 }}
    >
      <button
        className="absolute top-2 right-2 p-2 focus:outline-none"
        onClick={() => {
          track('free_user_promotion_modal_clicked', {
            method: 'Close',
          });

          handleClose();
        }}
      >
        <Icon as={X} size="xs" className="text-[#666666]" />
      </button>

      <div className="flex flex-col h-full justify-between">
        <div className="flex flex-col gap-3 p-5 pl-[26px]">
          <div className="flex flex-row gap-1 items-center">
            <TagIcon size={14} strokeWidth={2.5} />
            <span className="text-sm text-textfont-semibold">
              Limited time offer
            </span>
          </div>
          <span className="text-2xl font-semibold text-foreground leading-tight tracking-tight">
            50% off your first month
          </span>
          <h1 className="text-base font-medium text-foreground !tracking-tight">
            You're eligible for <strong>50% off</strong> your first month if you
            sign up for a paid plan in the next 24 hours.
          </h1>
          <h1 className="text-base font-medium text-foreground !tracking-tight">
            That's just $10 total.
          </h1>
        </div>

        <div className="flex flex-col gap-3 items-center px-3 pb-6">
          <Button className="w-full rounded-xl" onClick={onSubmit}>
            {isPending ? (
              <LoadingSpinner variant="small" color="gray" />
            ) : (
              'Accept offer'
            )}
          </Button>
          <span className="text-xs font-semibold text-textGray4">
            You won't be charged yet
          </span>
        </div>
      </div>
    </motion.div>
  );
};
